import React, { useEffect, useState } from "react";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INVITE_KEY = "raceHandicap.invite.v1";
const AGREED_KEY = "raceHandicap.betaAgreed.v1";
const COLS_KEY = "raceHandicap.colsUnlocked.v1";
const mono = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };
const serif = { fontFamily: "'Instrument Serif', 'Times New Roman', serif", fontWeight: 400 };

const post = (path, body) =>
  fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

const Shell = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="min-h-screen flex items-center justify-center px-5 py-10 bg-[#0a0a0a]"
    style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(132,204,22,0.08), transparent 55%)" }}
  >
    <div className="w-full max-w-md">{children}</div>
  </div>
);

const Brand = () => (
  <div className="flex items-center gap-3 mb-8">
    <img
      src={`${process.env.PUBLIC_URL}/logo-knight.png`}
      alt="RacetrackIQ knight"
      className="h-16 w-auto"
      style={{ filter: "drop-shadow(0 0 1px rgba(245,236,214,0.85)) drop-shadow(0 0 10px rgba(132,204,22,0.35))" }}
    />
    <div>
      <div className="text-3xl text-[#f5ecd6] leading-none" style={serif}>
        Racetrack<span className="text-[#84cc16]">IQ</span>
        <sup className="text-[11px] text-[#84cc16] ml-1 tracking-[0.2em]" style={mono}>-LIVE!</sup>
      </div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mt-1" style={mono}>
        Private beta
      </div>
    </div>
  </div>
);

const inputCls =
  "w-full h-11 px-3 rounded-md bg-[#111] border border-stone-700 text-stone-100 text-sm tracking-[0.12em] placeholder:text-stone-600 focus:outline-none focus:border-[#84cc16] transition-colors";
const btnCls =
  "w-full h-11 rounded-md bg-[#84cc16] text-[#0a0a0a] text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#a3e635] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2";

const GateScreen = ({ onEnter }) => {
  const [owner, setOwner] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (owner) {
        const r = await post("/unlock-columns", { password: value });
        if (!r.ok) throw new Error("Wrong owner password");
        localStorage.setItem(COLS_KEY, "1");
        localStorage.setItem("raceHandicap.ownerPw", value);
        onEnter("OWNER");
      } else {
        const r = await post("/invite/verify", { code: value });
        if (!r.ok) throw new Error("That invite code isn't valid or has been turned off");
        onEnter((await r.json()).code);
      }
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell testId="invite-gate">
      <Brand />
      <h1 className="text-4xl text-[#f5ecd6] mb-2" style={serif}>
        {owner ? "Owner sign-in" : "You're invited"}
      </h1>
      <p className="text-[12px] leading-6 text-stone-400 mb-6" style={mono}>
        {owner
          ? "Enter the owner password to open the app and manage invite codes."
          : "RacetrackIQ-Live! is in a private trial. Enter the invite code you were given."}
      </p>
      <form onSubmit={submit} className="space-y-3">
        <input
          type={owner ? "password" : "text"}
          value={value}
          onChange={(e) => setValue(owner ? e.target.value : e.target.value.toUpperCase())}
          placeholder={owner ? "Owner password" : "BETA-XXXX"}
          autoFocus
          data-testid={owner ? "owner-password-input" : "invite-code-input"}
          className={inputCls}
          style={mono}
        />
        {err && (
          <div className="text-[12px] text-[#f87171]" style={mono} data-testid="invite-error">
            {err}
          </div>
        )}
        <button type="submit" disabled={busy || !value.trim()} className={btnCls} style={mono} data-testid="invite-submit-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
          {owner ? "Sign in" : "Enter"}
        </button>
      </form>
      <button
        type="button"
        onClick={() => {
          setOwner((v) => !v);
          setValue("");
          setErr("");
        }}
        data-testid="invite-owner-toggle"
        className="mt-6 text-[10px] uppercase tracking-[0.2em] text-stone-600 hover:text-stone-300 transition-colors"
        style={mono}
      >
        {owner ? "← I have an invite code" : "Owner sign-in"}
      </button>
    </Shell>
  );
};

const POINTS = [
  ["Beta, for entertainment only", "This is a test version. Nothing here is wagering advice."],
  ["Data may be delayed or wrong", "Odds, probables and results come from public sites and can lag or be incorrect. Always verify with the official tote before betting."],
  ["21+ and wager responsibly", "You must be 21 or older (or your local legal age). If gambling stops being fun, call 1-800-GAMBLER."],
  ["Private trial", "Data comes from public racing sites. Please don't share the link or your invite code."],
  ["Tickets stay in this browser", "Saved tickets live only on this device and browser. Clearing browser data or switching devices loses them."],
];

const Disclaimer = ({ onAgree }) => {
  const [checked, setChecked] = useState(false);
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      data-testid="beta-disclaimer"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-stone-700 bg-[#0d0d0d] p-6 sm:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.7)]">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#84cc16] mb-2" style={mono}>
          <ShieldCheck size={13} /> Before you start
        </div>
        <h2 className="text-3xl text-[#f5ecd6] mb-5" style={serif}>
          Welcome to the beta
        </h2>
        <ul className="space-y-4 mb-6">
          {POINTS.map(([t, d], i) => (
            <li key={t} className="pl-3 border-l-2 border-[#84cc16]/70" data-testid={`disclaimer-point-${i}`}>
              <div className="text-[12px] font-bold uppercase tracking-[0.14em] text-stone-100" style={mono}>{t}</div>
              <div className="text-[12px] leading-5 text-stone-400 mt-0.5" style={mono}>{d}</div>
            </li>
          ))}
        </ul>
        <label className="flex items-start gap-3 mb-5 cursor-pointer select-none" style={mono}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="disclaimer-checkbox"
            className="mt-0.5 h-4 w-4 accent-[#84cc16]"
          />
          <span className="text-[12px] leading-5 text-stone-200">
            I'm 21+ (or of legal age where I live) and I understand the above.
          </span>
        </label>
        <button type="button" disabled={!checked} onClick={onAgree} className={btnCls} style={mono} data-testid="disclaimer-agree-btn">
          I agree — open the app
        </button>
      </div>
    </div>
  );
};

export const InviteGate = ({ children }) => {
  const [access, setAccess] = useState(() => localStorage.getItem(INVITE_KEY));
  const [checking, setChecking] = useState(() => {
    const c = localStorage.getItem(INVITE_KEY);
    return !!c && c !== "OWNER";
  });
  const [agreed, setAgreed] = useState(() => localStorage.getItem(AGREED_KEY) === "1");

  // Re-check friend codes on each visit so a turned-off code cuts access.
  useEffect(() => {
    if (!checking) return;
    post("/invite/verify", { code: access })
      .then((r) => {
        if (r.status === 403) {
          localStorage.removeItem(INVITE_KEY);
          setAccess(null);
        }
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [checking, access]);

  if (checking) {
    return (
      <Shell testId="invite-checking">
        <Loader2 className="mx-auto animate-spin text-[#84cc16]" />
      </Shell>
    );
  }
  if (!access) {
    return (
      <GateScreen
        onEnter={(code) => {
          localStorage.setItem(INVITE_KEY, code);
          setAccess(code);
        }}
      />
    );
  }
  return (
    <>
      {children}
      {!agreed && (
        <Disclaimer
          onAgree={() => {
            localStorage.setItem(AGREED_KEY, "1");
            setAgreed(true);
          }}
        />
      )}
    </>
  );
};

export const isOwner = () => localStorage.getItem(INVITE_KEY) === "OWNER";
